import { PRACTICES } from '../core/services/practices.service.ts';
import { getCurrentPracticeId } from '../core/services/practices.service.ts';
import { patientsService } from '../core/services/patients.service.ts';
import {
  addDays,
  startOfWeekMonday,
  todayLocal,
  toISODate,
} from './date-utils.ts';

export interface AgendaAppointment {
  id: string;
  practiceId: string;
  patientUuid: string;
  date: string;
  start: number;
  dur: number;
  patient: string;
  chairId: string;
  practitionerId: string;
  actId: string;
  notes?: string;
}

export interface AgendaChair {
  id: string;
  label: string;
  shortLabel: string;
}

export interface AgendaPractitioner {
  id: string;
  name: string;
  initials: string;
  color: string;
}

export type AgendaActType = 'consultation' | 'technique' | 'urgence';

export interface AgendaAct {
  id: string;
  label: string;
  color: string;
  duration: number;
  type: AgendaActType;
}

export interface AgendaAppointmentView extends AgendaAppointment {
  motif: string;
  color: string;
  practitioner: AgendaPractitioner;
  chair: AgendaChair;
  act: AgendaAct;
}

const CURRENT_WEEK_START = startOfWeekMonday(todayLocal());

export const AGENDA_CHAIRS: AgendaChair[] = [
  { id: 'chair-1', label: 'Fauteuil 1', shortLabel: 'F1' },
  { id: 'chair-2', label: 'Fauteuil 2', shortLabel: 'F2' },
  { id: 'chair-3', label: 'Fauteuil 3', shortLabel: 'F3' },
  { id: 'chair-4', label: 'Fauteuil 4', shortLabel: 'F4' },
];

export const AGENDA_PRACTITIONERS: AgendaPractitioner[] = [
  {
    id: 'dr-martin',
    name: 'Dr Martin',
    initials: 'DM',
    color: '#4f46e5',
  },
  {
    id: 'dr-benali',
    name: 'Dr Benali',
    initials: 'DB',
    color: '#0d9488',
  },
  {
    id: 'dr-leroy',
    name: 'Dr Leroy',
    initials: 'DL',
    color: '#db2777',
  },
];

export const DEFAULT_AGENDA_ACTS: AgendaAct[] = [
  {
    id: 'control',
    label: 'Contrôle',
    color: '#4f46e5',
    duration: 30,
    type: 'consultation',
  },
  {
    id: 'first-visit',
    label: 'Premier RDV',
    color: '#7c3aed',
    duration: 45,
    type: 'consultation',
  },
  {
    id: 'activation',
    label: 'Activation arc',
    color: '#0d9488',
    duration: 30,
    type: 'technique',
  },
  {
    id: 'aligners',
    label: 'Aligneurs',
    color: '#0284c7',
    duration: 30,
    type: 'technique',
  },
  {
    id: 'bonding',
    label: 'Pose bagues',
    color: '#ea580c',
    duration: 45,
    type: 'technique',
  },
  {
    id: 'scan',
    label: 'Scan intra-oral',
    color: '#0891b2',
    duration: 30,
    type: 'technique',
  },
  {
    id: 'retention',
    label: 'Contention',
    color: '#16a34a',
    duration: 30,
    type: 'technique',
  },
  {
    id: 'debonding',
    label: 'Débondage',
    color: '#db2777',
    duration: 45,
    type: 'technique',
  },
  {
    id: 'expansion',
    label: 'Expansion',
    color: '#84cc16',
    duration: 30,
    type: 'technique',
  },
  {
    id: 'emergency',
    label: 'Urgence',
    color: '#dc2626',
    duration: 20,
    type: 'urgence',
  },
];

const ACT_ROTATION = [
  'control',
  'activation',
  'aligners',
  'first-visit',
  'scan',
  'retention',
  'bonding',
  'emergency',
  'debonding',
  'expansion',
] as const;

const WEEKDAY_SLOTS = [
  8, 8.25, 8.5, 8.75, 9, 9.25, 9.5, 9.75, 10, 10.25, 10.5, 10.75, 11, 11.25,
  11.5, 14, 14.25, 14.5, 14.75, 15, 15.25, 15.5, 15.75, 16, 16.25, 16.5, 17,
];
const FRIDAY_SLOTS = [
  8, 8.5, 9, 9.5, 10, 10.5, 11, 14, 14.5, 15, 15.5, 16, 16.5,
];
const SATURDAY_SLOTS = [9, 9.5, 10, 10.5, 11, 11.5, 12];
const DURATIONS = [0.5, 0.5, 0.5, 0.75, 0.5, 0.25, 0.5, 0.5, 0.75, 0.5];

function slotsForDay(date: Date): number[] {
  const weekday = date.getDay();
  if (weekday === 0) return [];
  if (weekday === 6) return SATURDAY_SLOTS;
  if (weekday === 5) return FRIDAY_SLOTS;
  return WEEKDAY_SLOTS;
}

function buildAgendaAppointments(): AgendaAppointment[] {
  const appointments: AgendaAppointment[] = [];
  const rangeStart = addDays(CURRENT_WEEK_START, -21);
  const dayCount = 90;

  for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
    const date = addDays(rangeStart, dayIndex);
    const iso = toISODate(date);
    const slots = slotsForDay(date);
    const weekday = date.getDay();

    for (const practice of PRACTICES) {
      const patients = patientsService.list(practice.uuid);
      if (patients.length === 0) continue;

      slots.forEach((start, slotIndex) => {
        const seed = dayIndex * 31 + slotIndex * 13 + weekday;
        const patient = patients[seed % patients.length]!;
        const actId =
          ACT_ROTATION[(seed + slotIndex) % ACT_ROTATION.length] ?? 'control';
        const act = DEFAULT_AGENDA_ACTS.find((a) => a.id === actId)!;
        const chair =
          AGENDA_CHAIRS[
            (slotIndex + (weekday % 2) + dayIndex) % AGENDA_CHAIRS.length
          ]!;
        const practitioner =
          AGENDA_PRACTITIONERS[
            (dayIndex + slotIndex) % AGENDA_PRACTITIONERS.length
          ]!;

        appointments.push({
          id: `agenda-${practice.uuid.slice(0, 8)}-${iso}-${slotIndex}`,
          practiceId: practice.uuid,
          patientUuid: patient.uuid,
          date: iso,
          start,
          dur:
            slotIndex % 7 === 0
              ? act.duration / 60
              : DURATIONS[(dayIndex + slotIndex) % DURATIONS.length]!,
          patient: patient.name,
          chairId: chair.id,
          practitionerId: practitioner.id,
          actId,
        });
      });
    }
  }

  return appointments;
}

export const AGENDA_APPOINTMENTS: AgendaAppointment[] =
  buildAgendaAppointments();

export function appointmentsForDate(
  date: Date,
  practiceId?: string,
): AgendaAppointment[] {
  const iso = toISODate(date);
  const pid = practiceId ?? getCurrentPracticeId();
  return AGENDA_APPOINTMENTS.filter(
    (appointment) => appointment.date === iso && appointment.practiceId === pid,
  );
}

export function appointmentsForPatient(
  patientUuid: string,
  practiceId?: string,
  fromDate?: Date,
  limit = 8,
): AgendaAppointment[] {
  const pid = practiceId ?? getCurrentPracticeId();
  const fromIso = fromDate ? toISODate(fromDate) : undefined;
  return AGENDA_APPOINTMENTS.filter(
    (a) =>
      a.patientUuid === patientUuid &&
      a.practiceId === pid &&
      (!fromIso || a.date >= fromIso),
  )
    .sort((a, b) => a.date.localeCompare(b.date) || a.start - b.start)
    .slice(0, limit);
}

export function appointmentView(
  appointment: AgendaAppointment,
  acts: AgendaAct[] = DEFAULT_AGENDA_ACTS,
): AgendaAppointmentView {
  const act =
    acts.find((candidate) => candidate.id === appointment.actId) ??
    DEFAULT_AGENDA_ACTS[0]!;
  const chair =
    AGENDA_CHAIRS.find((candidate) => candidate.id === appointment.chairId) ??
    AGENDA_CHAIRS[0]!;
  const practitioner =
    AGENDA_PRACTITIONERS.find(
      (candidate) => candidate.id === appointment.practitionerId,
    ) ?? AGENDA_PRACTITIONERS[0]!;

  return {
    ...appointment,
    motif: act.label,
    color: act.color,
    chair,
    practitioner,
    act,
  };
}
